import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Warning as AlertTriangle,
  Briefcase,
  CalendarBlank as Calendar,
  Plus,
  Users,
  CaretRight as ChevronRight,
  Archive,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import { getGreeting } from '@/lib/greeting';
import { usePreferences } from '@/lib/preferences';
import { fetchUsers } from '@/lib/usersApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { PolicyNudge } from '@/components/shared/PolicyNudge';
import { StatCard } from '@/components/shared/StatCard';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useClerkWorkspace } from '@/lib/useClerkWorkspace';
import { listConflictReviews } from '@/lib/conflictReviewsApi';
import { useHearingsStore, type Hearing } from '@/lib/hearingsStore';
import { useMattersStore } from '@/lib/mattersStore';
import { useBarristersStore, barristerNameMatches } from '@/lib/barristersStore';
import { listAwaiting } from '@/lib/allocationsApi';
import { canSeeEventContent, isAdmin, redactedEventLabel, type PrincipalLite } from '@/lib/permissions';
import { fetchMyBarristers } from '@/lib/barristerClerksApi';
import { fetchAgedDebt } from '@/lib/feeNotesApi';
import { restoreMatter } from '@/lib/mattersApi';
import { PostHearingModal } from '@/components/shared/PostHearingModal';
import { AwaitingResponseTray } from '@/components/shared/AwaitingResponseTray';
import { ConferencesAwaitingTray } from '@/components/conferences/ConferencesAwaitingTray';
import { ConferenceCoordination } from '@/components/conferences/ConferenceCoordination';
import { ActivityLogPanel } from '@/components/shared/ActivityLogPanel';
import { AgendaPanel } from '@/components/shared/AgendaPanel';
import { toast } from 'sonner';
import { useDiaryStore, todayKey as getTodayKey } from '@/lib/diaryStore';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { Avatar } from '@/components/ui/avatar';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  ShieldCheck,
  Gavel,
  CalendarCheck,
} from '@phosphor-icons/react';

type Tab = 'overview' | 'actions' | 'barristers' | 'archive';

export default function ClerkDashboardPage() {
  // Same timezone as the barrister dashboard: the person's own, or the practice's.
  const { effective: { timezone: tz, locale } } = usePreferences();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';
  const basePath = '/clerk';

  const myBarristersQ = useQuery({
    queryKey: ['my-barristers'],
    queryFn: fetchMyBarristers,
    staleTime: 30_000,
  });

  // Fetch real chamber members for court attendee assignment
  const chamberMembersQ = useQuery({
    queryKey: ['chamber-members'],
    queryFn: () => fetchUsers({ limit: 100, status: 'active' }),
    staleTime: 60_000,
  });

  const { managedBarristers } = useClerkWorkspace();
  // Real mid-matter conflict reviews. This panel used to read a local store that nothing ever
  // wrote to, so it always said "all clear" and its Resolve/Dismiss changed nothing on the
  // server. The review queue (GET /conflicts/reviews) is senior-clerk/admin/silk only, so a
  // junior clerk does not query it.
  const canSeeConflictReviews = user?.memberType === 'senior_clerk';
  const conflictReviewsQ = useQuery({
    queryKey: ['conflict-reviews', 'pending'],
    queryFn: () => listConflictReviews('pending'),
    enabled: canSeeConflictReviews,
    staleTime: 30_000,
  });
  const clerkConflicts = conflictReviewsQ.data ?? [];

  const hearings = useHearingsStore((s) => s.hearings);
  const hearingsError = useHearingsStore((s) => s.error);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const allMatters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  // A FAILED LOAD IS NOT AN EMPTY DESK. Every tile below reads a store or query that falls back
  // to an empty list, so a failure used to render a confident `0` under "all assigned" and
  // "inbox clear" — a clerk starting their day on a broken connection was told there was
  // nothing to do. The Conflicts tile already said "could not load"; the other three did not.
  const mattersError = useMattersStore((s) => s.error);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);
  const unassignedMatters = allMatters.filter((m) => !m.barrister);

  // Archived matters within the clerk's scope. `allMatters` is already scoped by the
  // server (buildMatterScopeWhere → the barristers this clerk supports), so this consumes
  // the same canonical list every other matter panel reads — no separate access path.
  // Most-recently-touched first so the latest archivals surface at the top.
  const archivedMatters = allMatters
    .filter((m) => m.status === 'archived')
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const handleRestore = async (m: (typeof archivedMatters)[number]) => {
    setRestoringId(m.id);
    try {
      await restoreMatter(m.id);
      await fetchMatters();
      toast.success(`Restored — ${m.name}`);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          'Could not restore matter',
      );
    } finally {
      setRestoringId(null);
    }
  };
  const todayStr = getTodayKey();
  const todaysHearings = hearings.filter((h) => h.date === todayStr && !['completed', 'vacated', 'adjourned'].includes(h.status));
  const [postHearingTarget, setPostHearingTarget] = useState<Hearing | null>(null);

  const allEvents = useDiaryStore((s) => s.events);
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const wsConnected = useWebSocketStore((s) => s.connected);
  const tentativeEvents = allEvents.filter((e) => e.status === 'tentative');

  // Keep the clerk dashboard diary panel live — poll + WebSocket subscription
  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 300_000);
    return () => clearInterval(interval);
  }, [fetchEvents]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => fetchEvents());
    return unsub;
  }, [wsConnected, fetchEvents]);

  const barristerProfiles = useBarristersStore((s) => s.barristers);

  // Pending brief offers — from the real allocations API (pending + conference_requested)
  const awaitingAllocationsQ = useQuery({
    queryKey: ['allocations', 'awaiting'],
    queryFn: listAwaiting,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const pendingBookings = awaitingAllocationsQ.data ?? [];

  const agedDebtQ = useQuery({
    queryKey: ['aged-debt'],
    queryFn: fetchAgedDebt,
    staleTime: 300_000,
  });
  const overdueAmount = (agedDebtQ.data ?? []).reduce((s, r) => s + r.d30 + r.d60 + r.d90, 0);
  const overdueClients = (agedDebtQ.data ?? []).filter((r) => r.d30 + r.d60 + r.d90 > 0).length;

  // Hearings that occurred but have no outcome recorded — clerk is responsible for chasing
  const pendingOutcomeHearings = hearings.filter(
    (h) =>
      h.date < todayStr &&
      !h.outcome &&
      ['scheduled', 'confirmed', 'in_progress'].includes(h.status),
  );

  // Build court attendee list from real API: clerks + pupils
  type AttendeeRole = 'senior_clerk' | 'junior_clerk' | 'pupil';
  const ROLE_DISPLAY: Record<string, string> = { senior_clerk: 'Sr. Clerk', junior_clerk: 'Jr. Clerk', pupil: 'Pupil' };
  const chamberMembers = chamberMembersQ.data ?? [];
  const clerkMembers = chamberMembers
    .filter((m) => m.memberType === 'senior_clerk' || m.memberType === 'junior_clerk')
    .map((m) => ({
      name: `${m.firstName} ${m.lastName}`,
      label: `${m.firstName} (${ROLE_DISPLAY[m.memberType]})`,
      role: m.memberType as AttendeeRole,
    }));
  const pupilBarristers = barristerProfiles
    .filter((b) => b.role === 'pupil')
    .map((p) => ({
      name: p.name,
      label: `${p.shortName ?? p.name} (Pupil)`,
      role: 'pupil' as AttendeeRole,
    }));
  const courtAttendees = [...clerkMembers, ...pupilBarristers].filter((a) => a.name !== fullName);

  // Real senior clerks for junior clerk escalation notifications
  const realSeniorClerks = chamberMembers
    .filter((m) => m.memberType === 'senior_clerk' && `${m.firstName} ${m.lastName}` !== fullName)
    .map((m) => `${m.firstName} ${m.lastName}`);

  // Privacy principal for the logged-in clerk
  const principal: PrincipalLite = {
    name: fullName,
    role: user?.memberType === 'admin' ? 'admin' : 'clerk',
    memberType: user?.memberType,
  };
  const viewerIsAdmin = isAdmin(principal);

  type SlotInfo = { kind: 'hearing' | 'conference' | 'task' | 'personal' | 'free'; label: string; canSee: boolean };
  const masterDiaryRows = managedBarristers.map((mb) => {
    const profile = barristerProfiles.find((p) => p.name === mb.name);
    const rowLabel = profile?.shortName ?? mb.name;
    const todaysEvents = allEvents.filter(
      (e) => e.date === todayStr && (profile ? barristerNameMatches(e.barrister, profile) : e.barrister === mb.name),
    );
    const slots: SlotInfo[] = Array.from({ length: 8 }, () => ({ kind: 'free' as const, label: '', canSee: true }));
    for (const e of todaysEvents) {
      const canSee = viewerIsAdmin || canSeeEventContent(principal, e, allMatters, managedBarristers);
      const kind = (e.kind === 'hearing' || e.kind === 'conference' || e.kind === 'task' || e.kind === 'personal') ? e.kind : 'task';
      const label = canSee ? e.title : redactedEventLabel(e);
      const end = Math.min(e.start + e.span, 8);
      for (let i = Math.max(e.start, 0); i < end; i++) {
        slots[i] = { kind, label, canSee };
      }
    }
    return { id: profile?.id, name: rowLabel, slots };
  });

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const actionCount = clerkConflicts.length + tentativeEvents.length + todaysHearings.length;

  return (
    <div className="space-y-6">

      {/* ═══════════════════════════════════════════════════════
          NEXT 24 HOURS — unified Agenda Engine, scoped to the barristers this
          clerk supports (owner name shown per row). Live: reschedules reflect
          here without a manual reload. This is the clerk's 8:30am court view.
         ═══════════════════════════════════════════════════════ */}
      <AgendaPanel basePath={basePath} showBarrister />

      {/* ── Compact alert strip ── */}
      {(clerkConflicts.length > 0 || tentativeEvents.length > 0) && (
        <button
          type="button"
          onClick={() => setActiveTab('actions')}
          className="flex w-full items-center gap-4 rounded-lg border border-amber-200/80 dark:border-amber-500/30 bg-gradient-to-r from-amber-50/60 to-transparent px-5 py-3 text-left transition hover:border-amber-300 dark:border-amber-500/40"
        >
          {clerkConflicts.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-300">
              <ShieldCheck className="h-4 w-4" weight="fill" />
              {clerkConflicts.length} conflict{clerkConflicts.length > 1 ? 's' : ''}
            </span>
          )}
          {clerkConflicts.length > 0 && tentativeEvents.length > 0 && (
            <span className="h-3.5 w-px bg-border" />
          )}
          {tentativeEvents.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
              <CalendarCheck className="h-4 w-4" />
              {tentativeEvents.length} conference{tentativeEvents.length > 1 ? 's' : ''} awaiting confirmation
            </span>
          )}
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        </button>
      )}

      {/* ── Junior clerk court duty banner ── */}
      {user?.memberType === 'junior_clerk' && (
        <div className="flex items-center gap-3 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/10 px-5 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-orange-500 text-white">
            <Gavel className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground">Court Duty Mode</div>
            <div className="text-xs text-muted-foreground">Update hearing outcomes from court. Senior clerk is notified automatically.</div>
          </div>
          <Badge variant="warning">Junior</Badge>
        </div>
      )}

      {/* ── Header ── */}
      <PageHeader
        title={`${getGreeting(tz)}, ${user?.firstName}`}
        subtitle={new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: tz })}
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/clerk/bookings')}>
            New booking
          </Button>
        }
      />

      {/* ── Pending policy acknowledgements (clerks acknowledge chambers policies too) ── */}
      <PolicyNudge />

      {/* ── Awaiting response (live brief offers) ── */}
      <AwaitingResponseTray />

      {/* ── Conferences awaiting scheduling ── */}
      <ConferencesAwaitingTray />

      {/* ── Pending hearing outcomes — named list ── */}
      {pendingOutcomeHearings.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-amber-200/80 dark:border-amber-500/30 bg-gradient-to-r from-amber-50/70 to-transparent">
          <div className="flex items-center gap-3 px-5 py-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
              {pendingOutcomeHearings.length} hearing{pendingOutcomeHearings.length > 1 ? 's' : ''} awaiting outcome — chase barristers
            </span>
          </div>
          <div className="divide-y divide-amber-100/80 border-t border-amber-100">
            {pendingOutcomeHearings.slice(0, 4).map((h) => {
              const daysAgo = Math.floor((Date.now() - new Date(h.date).getTime()) / 86_400_000);
              return (
                <Link
                  key={h.id}
                  to={`/clerk/hearings/${h.id}`}
                  className="flex items-center justify-between px-5 py-2 hover:bg-amber-50/60 dark:bg-amber-500/10"
                >
                  <div>
                    <span className="text-xs font-medium text-foreground">{h.matterName}</span>
                    <span className="ml-2 text-[10px] text-brand-600 dark:text-brand-400">{h.matterNumber}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{h.leadBarrister}</span>
                  </div>
                  <span className={`text-xs font-semibold ${daysAgo >= 3 ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'}`}>
                    {daysAgo}d ago
                  </span>
                </Link>
              );
            })}
            {pendingOutcomeHearings.length > 4 && (
              <Link to="/clerk/hearings" className="block px-5 py-2 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50/60 dark:bg-amber-500/10">
                +{pendingOutcomeHearings.length - 4} more →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* A FAILED AGED-DEBT LOAD MUST NOT READ AS "NO DEBT". The banner below renders only when
          there IS overdue money, so when the fetch failed the whole money signal disappeared
          from the dashboard and nothing took its place — the quietest way to lose a figure. */}
      {agedDebtQ.isError && (
        <Link
          to="/clerk/recovery"
          className="flex w-full items-center gap-4 rounded-lg border border-amber-200/80 dark:border-amber-500/30 bg-gradient-to-r from-amber-50/70 to-transparent px-5 py-3 transition hover:border-amber-300"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
            Could not load overdue fee notes &mdash; open the recovery queue to check
          </span>
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        </Link>
      )}

      {/* ── Overdue debt alert (only shown when there is outstanding debt) ── */}
      {overdueAmount > 0 && (
        <Link
          to="/clerk/recovery"
          className="flex w-full items-center gap-4 rounded-lg border border-red-200/80 dark:border-red-500/30 bg-gradient-to-r from-red-50/70 to-transparent px-5 py-3 transition hover:border-red-300 dark:border-red-500/40"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />
          <span className="text-xs font-semibold text-red-700 dark:text-red-300">
            £{overdueAmount.toLocaleString('en-GB', { maximumFractionDigits: 0 })} overdue
            {overdueClients > 0 && ` across ${overdueClients} client${overdueClients > 1 ? 's' : ''}`}
            {' '}&mdash; view recovery queue
          </span>
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        </Link>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {/* On a failed load the VALUE becomes an em dash, not a zero: a number is a claim, and
            withholding the claim is the honest answer. The hint says which it is. */}
        <StatCard
          to="/clerk/conflict-reviews"
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Conflicts"
          value={conflictReviewsQ.isError && canSeeConflictReviews ? '\u2014' : clerkConflicts.length}
          hint={
            !canSeeConflictReviews
              ? 'reviewed by senior clerks'
              : conflictReviewsQ.isError
                ? 'could not load'
                : clerkConflicts.length > 0 ? 'awaiting a decision' : 'none awaiting a decision'
          }
        />
        <StatCard
          to="/clerk/matters"
          icon={<Briefcase className="h-5 w-5" />}
          label="Unassigned"
          value={mattersError ? '\u2014' : unassignedMatters.length}
          hint={
            mattersError
              ? 'could not load'
              : unassignedMatters.length > 0 ? 'matters waiting' : 'all assigned'
          }
        />
        <StatCard
          to="/clerk/hearings"
          icon={<Calendar className="h-5 w-5" />}
          label="Today's hearings"
          value={hearingsError ? '\u2014' : todaysHearings.length}
          hint={hearingsError ? 'could not load' : 'across all barristers'}
        />
        <StatCard
          to="/clerk/bookings"
          icon={<Users className="h-5 w-5" />}
          label="Pending briefs"
          value={awaitingAllocationsQ.isError ? '\u2014' : pendingBookings.length}
          hint={
            awaitingAllocationsQ.isError
              ? 'could not load'
              : pendingBookings.length > 0 ? 'to allocate or deliver' : 'inbox clear'
          }
        />
      </div>

      {/* ── Tab bar ── */}
      {/* Four tabs come to more than a phone is wide, so Archive — and its count of archived
          matters — sat past the right edge with no way to reach it. The strip scrolls sideways
          now, the same as the Fees and Reports strips. Both axes are named explicitly: the
          buttons carry no overlap pixel, so there is nothing protruding for `overflow-y` to
          find, and saying `hidden` stops it inventing a vertical scrollbar anyway. */}
      <div className="no-scrollbar -mb-px flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Overview
        </TabButton>
        <TabButton active={activeTab === 'actions'} onClick={() => setActiveTab('actions')} badge={actionCount || undefined}>
          Action items
        </TabButton>
        <TabButton active={activeTab === 'barristers'} onClick={() => setActiveTab('barristers')} badge={myBarristersQ.data?.length || managedBarristers.length || undefined}>
          Barristers
        </TabButton>
        <TabButton active={activeTab === 'archive'} onClick={() => setActiveTab('archive')} badge={archivedMatters.length || undefined}>
          Archive
        </TabButton>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          TAB: Overview — diary grid + live activity
         ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        /* `[&>*]:min-w-0`: a grid item's `min-width` is `auto`, so the single column this becomes
           below `lg` was sized by the Master diary's minimum content width — 547px inside a
           355px page. The diary lost 13:00 onwards and the Activity log lost the right of every
           entry, both CLIPPED by the shell rather than scrollable. Shrinkable items let the
           column be the width of the page, and the diary table scrolls inside its own box. */
        <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
          {/* Master diary */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Master diary — today</CardTitle>
                  <CardDescription>{new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz })}</CardDescription>
                </div>
                <Link to="/clerk/diary" className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                  Open diary <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="sticky left-0 bg-muted/30 px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Barrister
                      </th>
                      {['09', '10', '11', '12', '13', '14', '15', '16'].map((h) => (
                        <th key={h} className="px-2 py-2.5 text-center text-[10px] font-semibold text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {masterDiaryRows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-6 py-10 text-center text-sm text-muted-foreground">
                          No barristers currently managed. Configure scopes in settings.
                        </td>
                      </tr>
                    )}
                    {masterDiaryRows.map((row) => (
                      <tr
                        key={row.name}
                        className="cursor-pointer transition hover:bg-muted/30"
                        onClick={() => row.id ? navigate(`/clerk/barristers/${row.id}`) : navigate('/clerk/diary')}
                      >
                        <td className="sticky left-0 bg-card px-4 py-3 font-medium text-foreground">{row.name}</td>
                        {row.slots.map((slot, j) => {
                          const bg = slot.kind === 'hearing' ? 'bg-brand-200'
                            : slot.kind === 'conference' ? 'bg-amber-200'
                            : slot.kind === 'task' ? 'bg-violet-200'
                            : slot.kind === 'personal' ? 'bg-emerald-200'
                            : 'bg-muted/40';
                          return (
                            <td key={j} className="px-1 py-2">
                              <div
                                className={`mx-auto h-7 rounded-md ${bg} ${!slot.canSee && slot.kind !== 'free' ? 'opacity-60' : ''}`}
                                title={slot.label || slot.kind}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Six chips on one line is wider than a phone; "Free" and "Restricted" were off
                  the end of it. It wraps now, and is unchanged from the moment the row fits. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-brand-200" /> Hearing</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-200" /> Conference</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-violet-200" /> Task</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-200" /> Personal</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-muted/40" /> Free</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-brand-200 opacity-60" /> Restricted</span>
              </div>
            </CardContent>
          </Card>

          {/* Live activity feed */}
          <ActivityLogPanel
            title="Activity log"
            showSummary={true}
            limit={15}
            defaultFilter="all"
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          TAB: Action items — conflicts, conferences, court list
         ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'actions' && (
        <div className="space-y-6">
          {/* Conflicts */}
          {clerkConflicts.length > 0 && (
            <Card className="border-red-200 dark:border-red-500/30">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400" weight="fill" />
                  <CardTitle className="text-red-900 dark:text-red-200">Conflicts</CardTitle>
                  <Badge variant="danger">{clerkConflicts.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="divide-y divide-border">
                  {clerkConflicts.map((c) => (
                    <div key={c.id} className="flex items-start justify-between gap-4 px-6 py-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="danger">{c.severity}</Badge>
                          <span className="truncate text-sm font-semibold text-foreground">{c.case.caseName}</span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {c.case.caseNumber} · {c.regulatoryBasis ? 'Regulatory basis recorded — decision needed' : 'Regulatory basis not yet assessed'}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {/* The decision is made on the review screen, where the rules are enforced. */}
                        <Button variant="primary" size="sm" onClick={() => navigate('/clerk/conflict-reviews')}>
                          Open review
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conference coordination — real clerk workspace (replaces the old mock tray) */}
          <ConferenceCoordination />

          {/* Today's court list */}
          {todaysHearings.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gavel className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                    <CardTitle>Today's court list</CardTitle>
                    <Badge>{todaysHearings.length}</Badge>
                  </div>
                  <Link to="/clerk/diary" className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                    Master diary <ChevronRight className="inline h-3.5 w-3.5" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Matter</th>
                        <th>Type</th>
                        <th>Court</th>
                        <th>Barrister</th>
                        <th>Attending</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {todaysHearings.map((h) => (
                        <tr key={h.id}>
                          <td className="text-xs text-brand-700 dark:text-brand-300">{h.time}</td>
                          <td className="font-medium text-foreground">{h.matterName}</td>
                          <td><Badge>{h.type}</Badge></td>
                          <td className="text-xs text-muted-foreground">{h.court}</td>
                          <td>{h.leadBarrister}</td>
                          <td>
                            <SearchableSelect
                              value={h.courtAttendee ?? ''}
                              onChange={(v) => {
                                const attendee = courtAttendees.find((a) => a.name === v);
                                updateHearing(h.id, {
                                  courtAttendee: v || undefined,
                                  courtAttendeeRole: attendee?.role ?? 'none',
                                });
                                if (v) toast.success(`${v} assigned to ${h.matterName}`);
                              }}
                              placeholder="No one"
                              className="w-40 text-xs"
                              options={[
                                { value: '', label: 'No one' },
                                ...courtAttendees.map((a) => ({ value: a.name, label: a.label })),
                              ]}
                            />
                          </td>
                          <td>
                            <Button variant="primary" size="xs" onClick={() => setPostHearingTarget(h)}>
                              Update
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="py-12 text-center">
              <CardContent>
                <Gavel className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-muted-foreground">No action items right now</p>
                <p className="mt-1 text-xs text-muted-foreground">Conflicts, pending conferences and court updates will appear here</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          TAB: Barristers — managed barristers grid (real DB)
         ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'barristers' && (
        <div>
          {myBarristersQ.isLoading && (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!myBarristersQ.isLoading && (myBarristersQ.data?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              {myBarristersQ.data!.map((a) => {
                const { user: bu, practiceAreas } = a.barrister;
                const initials = `${bu.firstName[0]}${bu.lastName[0]}`;
                const areas = practiceAreas.slice(0, 3).map((pa) => pa.practiceArea.name);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => navigate(`/clerk/barristers/${a.barristerId}`)}
                    className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 text-left transition hover:border-brand-300 hover:shadow-card"
                  >
                    <Avatar src={bu.avatarUrl ?? undefined} fallback={initials} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {bu.firstName} {bu.lastName}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{bu.email}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        <Badge variant={a.role === 'primary' ? 'gold' : 'default'}>{a.role}</Badge>
                        {areas.map((area) => (
                          <span key={area} className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:text-brand-300">{area}</span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          ) : !myBarristersQ.isLoading ? (
            <Card className="py-12 text-center">
              <CardContent>
                <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-muted-foreground">No barristers assigned</p>
                <p className="mt-1 text-xs text-muted-foreground">Ask a chambers admin to assign you to barristers</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          TAB: Archive — archived matters within the clerk's scope
         ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'archive' && (
        <div>
          {archivedMatters.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Archived matters</CardTitle>
                  <Badge>{archivedMatters.length}</Badge>
                </div>
                <CardDescription>
                  Hidden from active lists. Open a matter for full history, or restore it to bring it back to active.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  {/* A table cannot reflow: on a phone its right-hand columns ran off the screen with no way to reach them. */}
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Matter</th>
                        <th>Ref</th>
                        <th>Barrister</th>
                        <th>Practice area</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedMatters.map((m) => (
                        <tr
                          key={m.id}
                          className="cursor-pointer transition hover:bg-muted/30"
                          onClick={() => navigate(`/clerk/matters/${m.id}`)}
                        >
                          <td className="font-medium text-foreground">{m.name}</td>
                          <td className="text-xs text-muted-foreground">{m.number}</td>
                          <td className="text-sm">{m.barrister || <span className="text-muted-foreground">Unassigned</span>}</td>
                          <td className="text-xs text-muted-foreground">{m.area || '—'}</td>
                          <td className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<ArrowCounterClockwise className="h-4 w-4" />}
                              disabled={restoringId === m.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleRestore(m);
                              }}
                            >
                              {restoringId === m.id ? 'Restoring…' : 'Restore'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="py-12 text-center">
              <CardContent>
                <Archive className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-muted-foreground">No archived matters</p>
                <p className="mt-1 text-xs text-muted-foreground">Matters you archive will appear here and can be restored anytime</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Post-Hearing Update Modal */}
      <PostHearingModal
        open={!!postHearingTarget}
        hearing={postHearingTarget}
        clerkName={fullName}
        onClose={() => setPostHearingTarget(null)}
        onSave={async (update) => {
          const saved = await updateHearing(update.hearingId, {
            status: update.result === 'adjourned' || update.result === 'part_heard' ? 'adjourned' : 'completed',
            outcome: update.outcome,       // free-text outcome summary
            outcomeType: update.outcomeType, // canonical type → drives follow-up tasks + reports
            judgmentReserved: update.judgmentReserved,
            costsOrdered: update.costsOrdered,
            orders: update.orders,         // judge's orders
            counselNotes: update.counselNotes,
            nextHearingDate: update.nextHearingDate,
            costsPosition: update.costsPosition,
            deadlines: update.deadlines,
          });
          if (!saved) return false; // rolled back; the store has told the clerk why — modal stays open

          // Matter status is now a backend consequence of recording the outcome
          // (conservative: only clean disposals close) — no fragile frontend jump.

          // Do NOT add a freeform diary event for the next hearing date — scheduling
          // a proper hearing via the hearings API creates the linked CalendarEvent
          // automatically. A freeform event here would create a duplicate.

          // Server sends notifications to the barrister and senior clerks automatically
          // when the underlying API calls (updateHearing, updateMatter) succeed.
          const isJuniorClerk = user?.memberType === 'junior_clerk';
          if (isJuniorClerk && realSeniorClerks.length > 0) {
            // Log to console so senior clerks know a court update was filed
            console.info(`[Court report] ${fullName} filed post-hearing update for ${postHearingTarget?.matterName}. Senior clerks to notify: ${realSeniorClerks.join(', ')}`);
          }

          setPostHearingTarget(null);
          return true;
        }}
      />
    </div>
  );
}

/* ── Tab button component ── */
function TabButton({ active, onClick, badge, children }: {
  active: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? 'border-b-2 border-brand-600 text-brand-600 dark:text-brand-400'
          : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
      {badge != null && badge > 0 && (
        <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15 px-1.5 text-[10px] font-bold text-red-700 dark:text-red-300">
          {badge}
        </span>
      )}
    </button>
  );
}

